import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useSiteFlags } from "@/contexts/SiteFlagsContext";

export default function RedeemGift() {
  const { code } = useParams();
  const nav = useNavigate();
  const { user, refresh: refreshAuth } = useAuth();
  const { refresh: refreshFlags } = useSiteFlags();
  const [gift, setGift] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [claiming, setClaiming] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/public/lifetime-gifts/lookup/${code}`);
        setGift(data);
      } catch (e) {
        setErr(e?.response?.data?.detail || "This gift link is invalid or has expired.");
      } finally {
        setLoading(false);
      }
    })();
  }, [code]);

  const claim = async () => {
    setClaiming(true);
    try {
      await api.post(`/lifetime-gifts/redeem/${code}`);
      toast.success("★ Lifetime membership unlocked — welcome to Recipe Filer!");
      await refreshAuth?.();
      await refreshFlags();
      setTimeout(() => nav("/dashboard"), 800);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not claim — try again.");
    } finally {
      setClaiming(false);
    }
  };

  if (loading) return <div className="container py-16 text-center text-rf-ink-soft">Looking up your gift…</div>;

  if (err) {
    return (
      <div className="container py-16 max-w-xl mx-auto text-center">
        <p className="overline text-rf-orange mb-3">Gift link</p>
        <h1 className="font-display text-4xl font-black mb-3">Hmm, no gift here.</h1>
        <p className="text-rf-ink-soft mb-6">{err}</p>
        <Link to="/" className="btn-ink">Go home</Link>
      </div>
    );
  }

  if (gift.cancelled) {
    return (
      <div className="container py-16 max-w-xl mx-auto text-center">
        <p className="overline text-rf-orange mb-3">Gift link</p>
        <h1 className="font-display text-4xl font-black mb-3">This gift was cancelled.</h1>
        <p className="text-rf-ink-soft mb-6">The sender cancelled this gift before it was claimed. Please reach out to them.</p>
        <Link to="/" className="btn-ink">Go home</Link>
      </div>
    );
  }

  if (gift.redeemed) {
    return (
      <div className="container py-16 max-w-xl mx-auto text-center">
        <p className="overline text-rf-orange mb-3">Gift link</p>
        <h1 className="font-display text-4xl font-black mb-3">Already claimed.</h1>
        <p className="text-rf-ink-soft mb-6">This gift has already been redeemed. If that wasn't you, contact the sender.</p>
        <Link to="/" className="btn-ink">Go home</Link>
      </div>
    );
  }

  return (
    <div className="container py-12 max-w-2xl mx-auto" data-testid="redeem-gift-page">
      <div className="bg-gradient-to-br from-rf-amber/20 to-rf-cream-alt rounded-3xl p-8 sm:p-12 text-center">
        <p className="overline text-rf-orange mb-3">A gift just for you</p>
        <h1 className="font-display text-4xl sm:text-5xl font-black mb-4 leading-tight">
          <span className="text-emerald-700">★ Lifetime</span> Recipe Filer membership
        </h1>
        <p className="text-rf-ink-soft text-lg mb-2">
          Hi <b className="text-rf-ink">{gift.recipient_name}</b> —
        </p>
        <p className="text-rf-ink-soft text-lg mb-6">
          <b className="text-rf-ink">{gift.gifter_name}</b> has gifted you a lifetime membership.<br />
          Unlimited recipes, unlimited cookbooks, every feature — free forever.
        </p>

        {gift.personal_message && (
          <blockquote className="border-l-4 border-rf-amber bg-white/60 text-left p-5 rounded-r-xl my-6 italic text-rf-ink-soft">
            "{gift.personal_message}"
            <p className="mt-2 not-italic text-xs text-rf-ink-soft">— {gift.gifter_name}</p>
          </blockquote>
        )}

        {user ? (
          <button
            data-testid="redeem-claim-btn"
            onClick={claim}
            disabled={claiming}
            className="btn-amber !text-base mt-4"
          >
            {claiming ? "Claiming…" : "★ Claim my lifetime gift"}
          </button>
        ) : (
          <div className="space-y-3 mt-6">
            <p className="text-sm text-rf-ink-soft">Sign in or register with <b>{gift.recipient_email}</b> to claim. Or use any account you prefer — the gift will attach to whoever claims it first.</p>
            <div className="flex flex-wrap gap-3 justify-center">
              <Link to={`/login?next=/redeem/${code}`} data-testid="redeem-login-link" className="btn-ink">Sign in to claim</Link>
              <Link to={`/register?next=/redeem/${code}`} data-testid="redeem-register-link" className="btn-amber">Create account &amp; claim</Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
